import React, { useState } from "react";
import { Star, MessageCircle, BadgeCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { imgUrl } from "@/utils/format";
import TTSButton from "@/components/TTSButton";
import PhotoLightbox from "@/components/PhotoLightbox";

function StarRow({ value }) {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-4 h-4 ${n <= value ? "fill-[#F4C430] text-[#F4C430]" : "text-[#E5E1D8]"}`} />
      ))}
    </div>
  );
}

export default function ReviewsList({ reviews }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("en") ? "en-US" : "fr-FR";
  const [lightbox, setLightbox] = useState(null); // { photos, index }
  if (!reviews || reviews.length === 0) {
    return (
      <div className="bg-white border border-sand rounded-2xl p-6 text-center text-mute">
        {t("reviews.emptyList")}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="reviews-list">
      {reviews.map((r) => (
        <div key={r.review_id} data-testid={`review-${r.review_id}`} className="bg-white border border-sand rounded-2xl p-4">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-elevated grid place-items-center font-display font-semibold">
                {r.user_name?.[0]?.toUpperCase() || "C"}
              </div>
              <div>
                <div className="font-semibold text-sm">{r.user_name}</div>
                <div className="text-xs text-mute">{new Date(r.created_at).toLocaleDateString(dateLocale, { day: "numeric", month: "long", year: "numeric" })}</div>
              </div>
            </div>
            <StarRow value={r.rating} />
          </div>
          {r.comment && <p className="text-sm text-ink mt-2 leading-relaxed">{r.comment}</p>}
          {r.photos?.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {r.photos.map((p, i) => (
                <button key={i} type="button" onClick={() => setLightbox({ photos: r.photos, index: i })} data-testid={`client-photo-${r.review_id}-${i}`} aria-label={`Agrandir la photo ${i+1}`}>
                  <img src={imgUrl(p)} alt={`photo du plat ${i+1}`} className="w-32 h-32 sm:w-36 sm:h-36 rounded-xl object-cover hover:opacity-90 transition-opacity cursor-zoom-in" />
                </button>
              ))}
            </div>
          )}
          {r.comment && (
            <div className="mt-2">
              <TTSButton
                text={r.comment}
                testId={`review-tts-${r.review_id}`}
                size="xs"
                ariaLabel={t("reviews.listenBy", { name: r.user_name })}
              />
            </div>
          )}
          {r.reply && (
            <div data-testid={`review-reply-${r.review_id}`} className="mt-3 ml-4 pl-3 border-l-2 border-brand-red bg-[#FFF5F6] rounded-r-xl p-3">
              <div className="flex items-center gap-2 text-xs text-brand-red mb-1 flex-wrap">
                <MessageCircle className="w-3 h-3" />
                <span className="font-semibold">{r.reply_author || t("reviews.restaurantReply")}</span>
                <span data-testid={`owner-reply-badge-${r.review_id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-[#E7F6EE] text-brand-green px-2 py-0.5 rounded-full font-semibold">
                  <BadgeCheck className="w-3 h-3" /> {t("reviews.ownerReplyBadge")}
                </span>
                {r.reply_at && <span className="text-mute">· {new Date(r.reply_at).toLocaleDateString(dateLocale, { day: "numeric", month: "long" })}</span>}
              </div>
              <p className="text-sm text-ink leading-relaxed">{r.reply}</p>
              {r.reply_photos?.length > 0 && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  {r.reply_photos.map((p, i) => (
                    <button key={i} type="button" onClick={() => setLightbox({ photos: r.reply_photos, index: i })} data-testid={`review-photo-${r.review_id}-${i}`} aria-label={`Agrandir la photo ${i+1}`}>
                      <img src={imgUrl(p)} alt={`photo ${i+1}`} className="w-32 h-32 sm:w-36 sm:h-36 rounded-xl object-cover hover:opacity-90 transition-opacity cursor-zoom-in" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      {lightbox && (
        <PhotoLightbox photos={lightbox.photos} initialIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
